import { Box, Button, Stack, Typography, keyframes, styled } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  UserNotification
} from 'services/notification';
import { getNotificationNavigationPath } from 'utils/notificationNavigation';

const PAGE_SIZE = 20;

export const NotificationsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightIdParam = searchParams.get('highlightNotificationId');
  const highlightId = highlightIdParam ? Number(highlightIdParam) : undefined;
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [highlighted, setHighlighted] = useState<number | undefined>(highlightId);
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const fetchList = useCallback(async () => {
    const res = await getNotifications({ page, size: PAGE_SIZE });
    setNotifications(res.notifications || []);
    setTotal(res.total || 0);
  }, [page]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const onRefresh = () => {
      fetchList();
    };
    window.addEventListener('notifications:refresh', onRefresh);
    return () => {
      window.removeEventListener('notifications:refresh', onRefresh);
    };
  }, [fetchList]);

  useEffect(() => {
    if (!highlightId) return;
    setHighlighted(highlightId);
    const timer = setTimeout(() => {
      setHighlighted(undefined);
    }, 3500);
    return () => clearTimeout(timer);
  }, [highlightId]);

  useEffect(() => {
    const onHighlight = (event: Event) => {
      const customEvent = event as CustomEvent<{ id?: number }>;
      const id = Number(customEvent.detail?.id);
      if (!id) return;
      setHighlighted(undefined);
      requestAnimationFrame(() => setHighlighted(id));
      const timer = setTimeout(() => setHighlighted(undefined), 3500);
      return () => clearTimeout(timer);
    };
    window.addEventListener('notifications:highlight', onHighlight as EventListener);
    return () => {
      window.removeEventListener('notifications:highlight', onHighlight as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!highlighted) return;
    const el = rowRefs.current[highlighted];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlighted, notifications]);

  const totalPages = useMemo(() => Math.max(Math.ceil(total / PAGE_SIZE), 1), [total]);

  const onClickNotification = useCallback(
    async (item: UserNotification) => {
      if (!item.isRead) {
        await markNotificationRead(item.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === item.id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
          )
        );
        window.dispatchEvent(new CustomEvent('notifications:refresh'));
      }
      navigate(getNotificationNavigationPath(item));
    },
    [navigate]
  );

  return (
    <WrapperPage>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <TitleStyled>Notifications</TitleStyled>
        <Button
          variant="outlined"
          onClick={async () => {
            await markAllNotificationsRead();
            setNotifications((prev) =>
              prev.map((item) => ({
                ...item,
                isRead: true,
                readAt: item.readAt || new Date().toISOString()
              }))
            );
            window.dispatchEvent(new CustomEvent('notifications:refresh'));
            await fetchList();
          }}>
          Mark all as read
        </Button>
      </Stack>

      <Stack gap={1.5}>
        {notifications.map((item) => (
          <NotificationRow
            key={item.id}
            ref={(el) => {
              rowRefs.current[item.id] = el;
            }}
            onClick={() => onClickNotification(item)}
            highlighted={highlighted === item.id}
            isRead={item.isRead}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={800}>{item.title}</Typography>
              <Typography fontSize={12} color="text.secondary">
                {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
              </Typography>
            </Stack>
            <Typography fontSize={14} color="text.primary">
              {item.body}
            </Typography>
            {!item.isRead && (
              <Box>
                <UnreadBadge>Unread</UnreadBadge>
              </Box>
            )}
          </NotificationRow>
        ))}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={1.5}>
        <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Prev
        </Button>
        <Typography fontSize={13}>
          {page} / {totalPages}
        </Typography>
        <Button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
          Next
        </Button>
      </Stack>
    </WrapperPage>
  );
};

const WrapperPage = styled(Stack)(() => ({
  padding: '54px 40px',
  width: '100%',
  minHeight: '100vh',
  gap: 20
}));

const TitleStyled = styled(Typography)(() => ({
  color: '#226AAA',
  fontSize: '24px',
  fontWeight: 900
}));

const highlightBorderPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.45);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(245, 158, 11, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
  }
`;

const NotificationRow = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'highlighted' && prop !== 'isRead'
})<{ highlighted?: boolean; isRead: boolean }>(({ highlighted, isRead }) => ({
  borderRadius: 12,
  border: `2px solid ${highlighted ? '#f59e0b' : '#e4e8ef'}`,
  padding: '14px 16px',
  cursor: 'pointer',
  background: highlighted ? '#fff8db' : isRead ? '#fff' : '#f3f9ff',
  transition: 'background-color .25s ease, border-color .25s ease',
  position: 'relative',
  animation: highlighted ? `${highlightBorderPulse} 1.1s ease-out 3` : 'none'
}));

const UnreadBadge = styled('span')(() => ({
  display: 'inline-block',
  background: '#226AAA',
  color: '#fff',
  fontSize: 10,
  borderRadius: 999,
  padding: '2px 8px',
  fontWeight: 700
}));
